import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, Camera, Video, Image, Share2 } from 'lucide-react';
import Navigation from '../components/home/Navigation';
import Footer from '../components/home/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PhotoPackagePage() {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      setError('Please fill in all fields.');
      return;
    }
    setStatus('submitting');
    setError('');
    try {
      const res = await fetch(`${API}/register-photo-package`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Registration failed');
      setStatus('success');
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setStatus('idle');
    }
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div className="min-h-screen bg-white" data-testid="photo-package-page">
      <Helmet>
        <title>Featured Guest Branding Package | TechBridge Digital Ball 2026</title>
        <meta name="description" content="Register for your complimentary Featured Guest Branding Package at the 26th Annual TechBridge Digital Ball, August 22, 2026. Professional portraits, on-camera introductions, and curated social-ready assets." />
      </Helmet>

      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-12">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#D97706] mb-4">
              26th Annual TechBridge Digital Ball · August 22, 2026
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#0B1A2B] leading-tight mb-6">
              Complimentary Featured Guest<br />Branding Package
            </h1>
            <p className="text-[#475569] text-base leading-relaxed max-w-2xl mx-auto">
              As a valued guest of the Digital Ball, you're invited to register for a complimentary professional branding package — courtesy of APIG and our partners.
            </p>
          </div>
        </div>
      </section>

      {/* Package Details */}
      <section className="pb-12">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto">
            <div className="border border-[#E2E8F0] bg-[#F8FAFC] p-8 sm:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-[#D97706]" />
                <div>
                  <h2 className="text-[#0B1A2B] font-medium text-lg">Featured Guest Branding Package</h2>
                  <p className="text-[#D97706] text-sm font-medium">$250 Value — Complimentary for Registered Guests</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Camera, text: 'Professional portraits and polished lifestyle images' },
                  { icon: Video, text: 'Short on-camera introduction: who they are, what they do, and the company they represent' },
                  { icon: Video, text: 'Vertical b-roll and candid video moments supporting their personal or company brand story' },
                  { icon: Image, text: 'Selected event images featuring the guest' },
                  { icon: Share2, text: 'Curated social-ready photo and video assets delivered digitally' },
                  { icon: Camera, text: 'Exclusive photo opportunity with event MC Ms. Aisha Bowe — NASA engineer turned entrepreneur and founder of LINGO, appearing as a guest of APIG' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <item.icon className="w-4 h-4 text-[#D97706] mt-1 flex-shrink-0" />
                    <p className="text-[#334155] text-sm leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="pb-24">
        <div className="container-luxury">
          <div className="max-w-xl mx-auto">
            {status === 'success' ? (
              <div className="text-center py-16" data-testid="registration-success">
                <div className="w-16 h-16 bg-[#D97706] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-serif text-3xl text-[#0B1A2B] mb-4">You're Registered</h2>
                <p className="text-[#475569] leading-relaxed">
                  Thank you for registering for the Featured Guest Branding Package. Our team will be in touch with details before the event. We look forward to seeing you at the Digital Ball.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="photo-registration-form">
                <h2 className="font-serif text-2xl text-[#0B1A2B] mb-2 text-center">Register Now</h2>
                <p className="text-[#64748B] text-sm text-center mb-8">Secure your complimentary branding package for the Digital Ball.</p>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#475569] uppercase tracking-wider mb-2">First Name</label>
                      <input
                        type="text"
                        value={form.first_name}
                        onChange={update('first_name')}
                        className="w-full bg-white border border-[#E2E8F0] text-[#0B1A2B] px-4 py-3 text-sm focus:border-[#D97706] focus:outline-none transition-colors"
                        placeholder="First name"
                        data-testid="input-first-name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#475569] uppercase tracking-wider mb-2">Last Name</label>
                      <input
                        type="text"
                        value={form.last_name}
                        onChange={update('last_name')}
                        className="w-full bg-white border border-[#E2E8F0] text-[#0B1A2B] px-4 py-3 text-sm focus:border-[#D97706] focus:outline-none transition-colors"
                        placeholder="Last name"
                        data-testid="input-last-name"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#475569] uppercase tracking-wider mb-2">Email Address</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update('email')}
                      className="w-full bg-white border border-[#E2E8F0] text-[#0B1A2B] px-4 py-3 text-sm focus:border-[#D97706] focus:outline-none transition-colors"
                      placeholder="your@email.com"
                      data-testid="input-email"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#475569] uppercase tracking-wider mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update('phone')}
                      className="w-full bg-white border border-[#E2E8F0] text-[#0B1A2B] px-4 py-3 text-sm focus:border-[#D97706] focus:outline-none transition-colors"
                      placeholder="(555) 000-0000"
                      data-testid="input-phone"
                      required
                    />
                  </div>
                </div>

                {error && <p className="text-red-500 text-sm mt-3" data-testid="form-error">{error}</p>}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full mt-6 bg-[#D97706] text-white py-3.5 text-sm font-medium tracking-wide hover:bg-[#B45309] transition-colors disabled:opacity-50 cursor-pointer"
                  data-testid="submit-registration"
                >
                  {status === 'submitting' ? 'Registering...' : 'Register for Complimentary Package'}
                </button>

                <p className="text-[#94A3B8] text-xs text-center mt-4">
                  Your information will be shared with the event photography team only.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Partner Firms */}
      <section className="pb-16">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-medium uppercase tracking-wider text-[#94A3B8] mb-8">Brought to you by</p>
            <div className="grid grid-cols-3 items-center justify-items-center max-w-xl mx-auto">
              <a href="https://osganalytics.com" target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity p-4">
                <img src={`${API}/resources/osg-logo.svg`} alt="OSG Analytics" style={{ width: '120px' }} />
              </a>
              <a href="https://brackett.partners" target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity p-4">
                <img src={`${API}/resources/brackett-logo.svg`} alt="Brackett Partners" style={{ width: '120px' }} />
              </a>
              <a href="https://harpercookconsultingllc.com" target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity p-4">
                <img src={`${API}/resources/harpercook-logo.png`} alt="Harper Cook Consulting" style={{ width: '120px' }} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
